import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { SavedTradesProvider } from "@/contexts/SavedTradesProvider";
import { FollowedEntitiesProvider } from "@/contexts/FollowedEntitiesProvider";
import { WatchlistProvider } from "@/contexts/WatchlistProvider";
import { AlertedTradesProvider } from "@/contexts/AlertedTradesProvider";
import Index from "./pages/Index";
import LoadingShell from "./components/LoadingShell";
import { AnalyticsTracker } from "./components/AnalyticsTracker";

const Congress = lazy(() => import("./pages/Congress"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Stock = lazy(() => import("./pages/Stock"));
const Insider = lazy(() => import("./pages/Insider"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const ClusterBuys = lazy(() => import("./pages/ClusterBuys"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Following = lazy(() => import("./pages/Following"));

const News = lazy(() => import("./pages/News"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Signals = lazy(() => import("./pages/Signals"));
const About = lazy(() => import("./pages/About"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const TrackRecord = lazy(() => import("./pages/TrackRecord"));
const Politician = lazy(() => import("./pages/Politician"));
const Settings = lazy(() => import("./pages/Settings"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Earnings = lazy(() => import("./pages/Earnings"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));


const NotFound = lazy(() => import("./pages/NotFound"));
const Toaster = lazy(async () => {
  const module = await import("@/components/ui/toaster");
  return { default: module.Toaster };
});
const Sonner = lazy(async () => {
  const module = await import("@/components/ui/sonner");
  return { default: module.Toaster };
});

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <FollowedEntitiesProvider>
        <WatchlistProvider>
        <SavedTradesProvider>
        <AlertedTradesProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
        <BrowserRouter>
          <AnalyticsTracker />
          <main id="main-content">
            <Suspense fallback={<LoadingShell variant="route" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/congress" element={<Congress />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/stock/:ticker" element={<Stock />} />
                <Route path="/insider/:name" element={<Insider />} />
                <Route path="/watchlist" element={<Watchlist />} />
                <Route path="/cluster-buys" element={<ClusterBuys />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/following" element={<Following />} />
                
                <Route path="/news" element={<News />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/signals" element={<Signals />} />
                <Route path="/significant-trades" element={<Signals />} />
                {/* Legacy slug — kept so external links and indexed results don't 404 */}
                <Route path="/smart-signals" element={<Navigate to="/significant-trades" replace />} />
                <Route path="/about" element={<About />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/track-record" element={<TrackRecord />} />
                <Route path="/politician/:name" element={<Politician />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/earnings" element={<Earnings />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </BrowserRouter>
        </AlertedTradesProvider>
        </SavedTradesProvider>
        </WatchlistProvider>
        </FollowedEntitiesProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
